import { updatePassword, sendPasswordResetEmail, createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase/config'; // Adjusted path to firebase config
// Cloud Functions are now called via standard fetch, not httpsCallable

/**
 * Retrieves the Firebase Authentication ID token for the current user.
 * @param {boolean} forceRefresh - If true, forces a token refresh. Defaults to true.
 * @returns {Promise<string|null>} The ID token, or null if no user is logged in or an error occurs.
 */
export const getAuthToken = async (forceRefresh = true) => {
  const currentUser = auth.currentUser;
  if (currentUser) {
    try {
      const token = await currentUser.getIdToken(forceRefresh);
      return token;
    } catch (error) {
      console.error('Error getting auth token:', error);
      // Depending on the error, you might want to handle it differently
      // For example, if the token is expired and refresh fails, you might sign the user out.
      return null;
    }
  }
  console.warn('No current user found to get auth token.');
  return null; // No user logged in
};

// Example: Function to observe auth state changes (optional, might be in AuthContext)
export const onAuthChange = (callback) => {
  return auth.onAuthStateChanged(callback);
};

/**
 * Updates the password of the currently logged-in user.
 * @param {string} newPassword - The new password.
 * @returns {Promise<void>}
 */
export const updateUserPassword = async (newPassword) => {
  const user = auth.currentUser;
  if (user) {
    try {
      await updatePassword(user, newPassword);
      console.log('Password updated successfully!');
    } catch (error) {
      console.error('Error updating password:', error);
      throw error;
    }
  } else {
    throw new Error('No user is currently logged in.');
  }
};

/**
 * Sends a password reset email to the specified email address.
 * @param {string} email - The email address to send the reset link to.
 * @returns {Promise<void>}
 */
export const sendPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    console.log('Password reset email sent!');
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw error;
  }
};

/**
 * Creates a new user with the provided email and password.
 * This function is typically used by an administrator.
 * @param {string} email - The email for the new user.
 * @param {string} password - The password for the new user.
 * @returns {Promise<UserCredential>}
 */
const API_BASE_URL = 'https://api.fuelsolution.in/api';

/**
 * Fetches a list of all users from the backend via standard fetch.
 * @returns {Promise<Array>}
 */
export const listAllUsers = async () => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error("Authentication required.");
  }

  try {
    const response = await fetch(`${API_BASE_URL}/users`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Failed to list users: ${response.status} ${errorData}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error calling listUsers function:", error);
    throw error;
  }
};

/**
 * Deletes a user by their UID via standard fetch.
 * @param {string} uid - The UID of the user to delete.
 * @returns {Promise<void>}
 */
export const deleteUserByUid = async (uid) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error("Authentication required.");
  }

  try {
    const response = await fetch(`${API_BASE_URL}/users/${uid}`, {
      method: "DELETE",
      headers: {
        "Authorization": `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Failed to delete user: ${response.status} ${errorData}`);
    }
  } catch (error) {
    console.error("Error calling deleteUser function:", error);
    throw error;
  }
};

export const createNewUser = async (email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    console.log('New user created:', userCredential.user);
    return userCredential;
  } catch (error) {
    console.error('Error creating new user:', error);
    throw error;
  }
};
